import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { useResolvedById } from "@/shared/hooks/useResolvedById";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { graduationService } from "@/features/graduations/services/graduation.service";
import type { GraduationResponseDTO } from "@/features/graduations/types/graduation.types";

interface GraduationAsyncSelectProps {
  value: number | null;
  onChange: (graduationId: number | null) => void;
  disabled?: boolean;
  inputId?: string;
}

const DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 8;

function formatGraduationLabel(graduation: GraduationResponseDTO): string {
  const date = new Date(graduation.eventDate);
  if (Number.isNaN(date.getTime())) return graduation.eventName;
  const formatted = new Intl.DateTimeFormat("pt-BR").format(date);
  return `${graduation.eventName} · ${formatted}`;
}

export function GraduationAsyncSelect({
  value,
  onChange,
  disabled = false,
  inputId,
}: GraduationAsyncSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<GraduationResponseDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchGraduationById = useCallback(
    (id: number, signal: AbortSignal) =>
      graduationService.getById(id, { signal }),
    [],
  );
  const [selected, preloadGraduation, resolveError] = useResolvedById(
    value,
    fetchGraduationById,
  );

  useEffect(() => {
    if (resolveError) {
      toast.error(
        getApiErrorMessage(resolveError, "Erro ao carregar formatura"),
      );
    }
  }, [resolveError]);

  const fetchGraduations = useCallback(
    async (term: string, signal: AbortSignal) => {
      const response = await graduationService.search(
        {
          eventName: term,
          size: SEARCH_PAGE_SIZE,
          sortBy: "eventDate",
          sortDirection: "DESC",
        },
        { signal },
      );
      return response.content;
    },
    [],
  );

  useDebouncedSearch({
    term: searchTerm,
    fetcher: fetchGraduations,
    setItems: setResults,
    setLoading: setSearching,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar formaturas")),
    debounceMs: DEBOUNCE_MS,
  });

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleSelect = useCallback(
    (graduation: GraduationResponseDTO) => {
      preloadGraduation(graduation.idGraduation, graduation);
      onChange(graduation.idGraduation);
      setSearchTerm("");
      setResults([]);
      setOpen(false);
    },
    [onChange, preloadGraduation],
  );

  const handleClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  if (selected) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm">
        <span className="flex-1">{formatGraduationLabel(selected)}</span>
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          aria-label="Trocar formatura"
          className="rounded-full p-0.5 hover:bg-accent"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          id={inputId}
          value={searchTerm}
          placeholder="Buscar formatura pelo nome do evento…"
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          className="pl-9"
        />
      </div>

      {open && searchTerm.trim().length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto z-50">
          {searching ? (
            <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
              <Spinner className="w-4 h-4" />
              Buscando…
            </div>
          ) : results.length === 0 ? (
            <div className="p-3 text-center text-sm text-muted-foreground">
              Nenhuma formatura encontrada
            </div>
          ) : (
            results.map((graduation) => (
              <button
                key={graduation.idGraduation}
                type="button"
                onClick={() => handleSelect(graduation)}
                className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
              >
                {formatGraduationLabel(graduation)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
